// Daten der Sonstige-Detailseite (Handoff Sonstige Kontakte §2): Kopf aus kontakte_unified und Zeile aus kontakte –
// dieselben Abfragen wie bisher in KontaktDetail. Dazu die Zähler der Kontext-Sidebar (Verknüpft, Aktivität, Notizen)
// über die bestehenden Ladefunktionen (nur lesend). Lädt neu bei KONTAKT_GEAENDERT.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { ladeAktivitaeten, ladeNotizen, ladeVerknuepfungen } from './api';
import { KONTAKT_GEAENDERT, meldeKontaktGeaendert } from './detailTab';
import { verknuepfungAnzeige } from './verknuepfung';

export interface SonstigerKontaktKopf {
  anzeigename: string;
  email: string | null;
  telefon: string | null;
  typ_label: string;
}

/** Zeile der Tabelle kontakte (Spalten laut DB, 19.10.2026). */
export interface SonstigerKontaktZeile {
  id: string;
  typ: string | null;
  anrede: string | null;
  vorname: string | null;
  nachname: string | null;
  firma_name: string | null;
  kontaktart: string | null;
  unternehmensart: string | null;
  geburtsdatum: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  stadt: string | null;
  land: string | null;
  email: string | null;
  festnetz: string | null;
  handynummer: string | null;
  kontoinhaber: string | null;
  bank: string | null;
  iban: string | null;
  bic: string | null;
  steuernummer: string | null;
  ust_id_nr: string | null;
  glaeubiger_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** aktiv/inaktiv (Migration 2026-10-19-kontakte-status) */
  status?: string | null;
  /** Nicht in der Tabelle; KontaktDetail las sie dennoch (Stammdaten) – bleiben leer. */
  telefon?: string | null;
  mobil?: string | null;
}

export interface KontaktAnzahl { verknuepft: number; aktivitaet: number; notizen: number }

export type SonstigerKontaktZustand =
  | { status: 'laedt' }
  | { status: 'fehlt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; kopf: SonstigerKontaktKopf; zeile: SonstigerKontaktZeile };

function meldungAus(err: unknown): string {
  if (err instanceof Error) return err.message;
  const m = (err as { message?: unknown } | null)?.message;
  return typeof m === 'string' ? m : 'Unbekannter Fehler';
}

/** Zähler wie in den Tabs: Verknüpfungen nach verknuepfungAnzeige, Aktivitäten und Notizen vollständig. */
export async function ladeKontaktAnzahl(id: string): Promise<KontaktAnzahl> {
  const [links, akt, notizen] = await Promise.all([ladeVerknuepfungen('kontakt', id), ladeAktivitaeten('kontakt', id), ladeNotizen('kontakt', id)]);
  return {
    verknuepft: links.filter(l => verknuepfungAnzeige(l, { typ: 'kontakt', id }) !== null).length,
    aktivitaet: akt.length,
    notizen: notizen.length,
  };
}

/** mitAnzahl: Zähler laden (Sidebar immer, Seite nur mobil für das Segment). */
export function useSonstigerKontakt(id: string | null, mitAnzahl = true): {
  zustand: SonstigerKontaktZustand;
  anzahl: KontaktAnzahl | null;
  neuLaden: () => void;
} {
  const [zustand, setZustand] = useState<SonstigerKontaktZustand>({ status: 'laedt' });
  const [anzahl, setAnzahl] = useState<KontaktAnzahl | null>(null);
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    const neu = () => setRunde(r => r + 1);
    window.addEventListener(KONTAKT_GEAENDERT, neu);
    return () => window.removeEventListener(KONTAKT_GEAENDERT, neu);
  }, []);

  useEffect(() => {
    if (!id) return;
    let abbruch = false;
    setZustand(z => (z.status === 'ok' ? z : { status: 'laedt' }));
    Promise.all([
      supabase.from('kontakte_unified').select('anzeigename, email, telefon, typ_label').eq('quelle', 'kontakt').eq('quelle_id', id).maybeSingle(),
      supabase.from('kontakte').select('*').eq('id', id).maybeSingle(),
    ]).then(([kopf, zeile]) => {
      if (abbruch) return;
      const fehler = kopf.error ?? zeile.error;
      if (fehler) { setZustand({ status: 'fehler', meldung: meldungAus(fehler) }); return; }
      if (!kopf.data || !zeile.data) { setZustand({ status: 'fehlt' }); return; }
      setZustand({ status: 'ok', kopf: kopf.data as SonstigerKontaktKopf, zeile: zeile.data as SonstigerKontaktZeile });
    }).catch((err: unknown) => { if (!abbruch) setZustand({ status: 'fehler', meldung: meldungAus(err) }); });
    if (mitAnzahl) ladeKontaktAnzahl(id).then(a => { if (!abbruch) setAnzahl(a); }).catch(() => { if (!abbruch) setAnzahl(null); });
    return () => { abbruch = true; };
  }, [id, runde, mitAnzahl]);

  // Wechsel des Kontakts: alten Stand nicht zeigen
  useEffect(() => { setZustand({ status: 'laedt' }); setAnzahl(null); }, [id]);

  const neuLaden = useCallback(() => meldeKontaktGeaendert(), []);
  return { zustand, anzahl, neuLaden };
}
